// frontend/src/components/WorkshopDashboard/AssignedVehiclesTable.js
// Replaces MaintenanceAlerts.js — which mapped every vehicle, returned null inside the
// map (emitting empty <tr>s), then ran a second full pass for the "All Good" check.
// Also drops the "All vehicles are healthy! 🚗💨" emoji copy.

import { useState } from 'react';
import { IconCar, IconChevron, IconPencil, IconDoc } from '../icons/Icons';
import { fmt } from '../../theme/iparc';
import { oilStatus, oilDue } from '../../hooks/useWorkshopHealth';

const COLS = '146px minmax(0,1.3fr) minmax(0,1.1fr) 112px minmax(0,1.4fr) 104px';
const HEAD = ['Immatriculation', 'Véhicule', 'Client', 'Odomètre', 'Prochaine vidange', 'Action'];

export default function AssignedVehiclesTable({
  vehicles, health, onLogMaintenance, onCreateQuote, onOpenVehicle, onSeeAll, limit = 6,
}) {
  const [filter, setFilter] = useState('all');

  const shown = vehicles
    .filter(v => filter === 'all' ? true : filter === 'due' ? oilDue(v) : !oilDue(v))
    .slice(0, limit);

  const chips = [
    { key: 'all', label: `Tous · ${health.vehicleCount}` },
    { key: 'due', label: `Vidange due · ${health.dueCount}` },
    { key: 'ok',  label: `À jour · ${health.okCount}` },
  ];

  return (
    <section className="rounded-2xl border border-[#241F1B] bg-gradient-to-b from-[#17140F] to-[#131110] overflow-x-auto overflow-y-hidden">
      <header className="flex flex-wrap items-center gap-3.5 px-[18px] py-[15px] border-b border-[#241F1B] min-w-[860px]">
        <h2 className="font-['Space_Grotesk',sans-serif] text-[14.5px] font-semibold tracking-[-.01em] text-[#F4F1EA] whitespace-nowrap">
          Véhicules confiés
        </h2>
        <div className="flex gap-[5px]">
          {chips.map(c => (
            <button key={c.key} onClick={() => setFilter(c.key)}
              className={`h-[26px] px-2.5 rounded-[7px] border border-[#2B2724] text-[11.5px] font-medium whitespace-nowrap transition-colors ${
                filter === c.key ? 'bg-[#2B2724] text-[#F4F1EA]' : 'bg-transparent text-[#8F877B] hover:text-[#C9C3B6]'
              }`}>
              {c.label}
            </button>
          ))}
        </div>
        <button onClick={onSeeAll}
          className="ml-auto text-[11.5px] text-[#8F877B] hover:text-[#F4F1EA] whitespace-nowrap transition-colors">
          Tous les véhicules assignés →
        </button>
      </header>

      <div className="grid gap-3.5 px-[18px] py-2.5 border-b border-[#241F1B] bg-[#121110] min-w-[860px]"
           style={{ gridTemplateColumns: COLS }}>
        {HEAD.map((h, i) => (
          <span key={h} className={`text-[9.5px] uppercase tracking-[.16em] text-[#5C554C] ${i === 3 || i === 5 ? 'text-right' : ''}`}>
            {h}
          </span>
        ))}
      </div>

      {shown.length === 0 ? (
        <p className="px-[18px] py-8 text-[12.5px] text-[#6E665C] min-w-[860px]">Aucun véhicule dans cette vue.</p>
      ) : shown.map(v => {
        const oil = oilStatus(v);
        return (
          <div key={v._id}
               className="grid gap-3.5 px-[18px] py-3 border-b border-[#1E1B18] items-center min-w-[860px] hover:bg-[#1A1613] transition-colors"
               style={{ gridTemplateColumns: COLS }}>

            <div className="flex items-center gap-2.5 min-w-0">
              <span className="w-7 h-7 rounded-lg bg-[#241F1B] text-[#A79E90] flex items-center justify-center shrink-0">
                <IconCar className="w-[15px] h-[15px]" />
              </span>
              <span className="font-['Space_Grotesk',sans-serif] text-[13px] font-semibold tracking-[.02em] whitespace-nowrap text-[#F4F1EA]">
                {v.plateNumber}
              </span>
            </div>

            <div className="min-w-0">
              <p className="text-[12.5px] text-[#F4F1EA] truncate">{v.brand} {v.model}</p>
              <p className="text-[11px] text-[#6E665C] mt-0.5 truncate">
                {v.currentJob || v.lastServiceType || 'Aucune intervention en cours'}
              </p>
            </div>

            <p className="min-w-0 text-xs text-[#C9C3B6] truncate">
              {v.owner?.name || v.ownerName || '—'}
            </p>

            <div className="text-right font-['Space_Grotesk',sans-serif] text-[13px] tabular-nums text-[#C9C3B6]">
              {fmt(v.odometerReading)}{' '}
              <span className="text-[10.5px] text-[#6E665C]">{v.distanceUnit === 'mi' ? 'mi' : 'km'}</span>
            </div>

            <div className="min-w-0">
              <div className="flex justify-between items-baseline mb-[5px]">
                <span className="font-['Space_Grotesk',sans-serif] text-xs tabular-nums text-[#A79E90] whitespace-nowrap">
                  {fmt(v.nextOilChangeOdometer)} {v.distanceUnit === 'mi' ? 'mi' : 'km'}
                </span>
                <span className="text-[10.5px] ml-2 truncate" style={{ color: oil.color }}>{oil.label}</span>
              </div>
              <span className="block h-1 rounded-[3px] bg-[#241F1B] overflow-hidden">
                <span className="block h-full rounded-[3px]" style={{ width: oil.pct, background: oil.color }} />
              </span>
            </div>

            <div className="flex justify-end gap-1.5">
              <button title="Saisir un entretien" onClick={() => onLogMaintenance?.(v)}
                className="w-7 h-7 rounded-[7px] border border-[#2B2724] hover:border-[#4E4740] bg-[#141210]
                           text-[#8F877B] hover:text-[#F4F1EA] flex items-center justify-center transition-colors">
                <IconPencil className="w-3.5 h-3.5" />
              </button>
              <button title="Établir un devis" onClick={() => onCreateQuote?.(v)}
                className="w-7 h-7 rounded-[7px] border border-[#2B2724] hover:border-[#4E4740] bg-[#141210]
                           text-[#8F877B] hover:text-[#F4F1EA] flex items-center justify-center transition-colors">
                <IconDoc className="w-3.5 h-3.5" />
              </button>
              <button title="Ouvrir la fiche" onClick={() => onOpenVehicle?.(v)}
                className="w-7 h-7 rounded-[7px] border border-[#2B2724] hover:border-[#4E4740] bg-[#141210]
                           text-[#8F877B] hover:text-[#F4F1EA] flex items-center justify-center transition-colors">
                <IconChevron className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        );
      })}

      {vehicles.length > limit && (
        <button onClick={onSeeAll}
          className="w-full py-3 text-[12px] text-[#8F877B] hover:text-[#F4F1EA] min-w-[860px] transition-colors">
          Voir les {vehicles.length} véhicules →
        </button>
      )}
    </section>
  );
}
